import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import { withFeeScheduleCategoryIdentity } from "_shared/fee-schedules/categories/fee-schedule-template-category";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";

/** What to do with one category picked from Select Existing Categories on an active proposal. */
export type ExistingCategoryMerge = {
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
  /** The user agreed to replace the fee schedule's category of the same name. */
  overwrite: boolean;
};

/**
 * The merges, made on the fee schedule redline as it stands now.
 *
 * A picked category with no match is added. One that matches is replaced only when the user agreed
 * to overwrite it, so a category of that name that a collaborator added after the user was asked is
 * kept.
 *
 * A replacement edits the matched row, found by its own id. `getMatchingCategoryRedline` matches by
 * id or by name, and a picked category usually matches by name: a published rate always arrives
 * with an id of its own (`FeeSchedule.copyCategoryForProposal`). Replacing by the picked category's
 * id therefore found no row and threw. The replacement also takes the row's identity
 * (`withFeeScheduleCategoryIdentity`), so the save updates that item rather than removing it and
 * adding another, and its comments and history stay with it.
 *
 * `mergedCategories` are the categories that went onto the fee schedule, as they went: the ones a
 * caller may seat people for.
 */
export function withExistingCategoryMerges(
  feeScheduleRedline: FeeScheduleRedline,
  merges: readonly ExistingCategoryMerge[],
): { feeScheduleRedline: FeeScheduleRedline; mergedCategories: ExistingCategoryMerge["category"][] } {
  let newRedline = feeScheduleRedline;
  const mergedCategories: ExistingCategoryMerge["category"][] = [];
  for (const { category, overwrite } of merges) {
    const matchingRow = newRedline.getMatchingCategoryRedline(category);
    if (!matchingRow) {
      newRedline = newRedline.addEntry(category);
      mergedCategories.push(category);
      continue;
    }
    const matchingRowId = matchingRow.field.id;
    const matchingCategory = matchingRow.currentEntry ?? matchingRow.revisedEntry ?? matchingRow.originalEntry;
    if (overwrite && matchingRowId && matchingCategory) {
      const replacement = withFeeScheduleCategoryIdentity(category, matchingCategory);
      newRedline = newRedline.replaceEntryById(matchingRowId, replacement);
      mergedCategories.push(replacement);
    }
  }
  return { feeScheduleRedline: newRedline, mergedCategories };
}
